import { getTokenHeader } from "@/helpers/commonHelper";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
import axios from "axios";
import APIKeyList from "@/components/Common/APIKeyList/APIKeyList.vue";
import ValidationDialog from "@/components/Common/ValidationDialog/ValidationDialog.vue";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import _ from "lodash";
import InfoDialog from "@/components/Common/InfoDialog/InfoDialog.vue";

const defaultConfig = {
  submandants: [],
  ragGroups: [],
  ipWhitelist: "",
  isDeactivated: false,
  deactivatedText: "",
  isMonitored: false,
  monitoredWords: "",
  informAddresses: "",
  isSleepEnabled: false,
  sleepFromHour: 22,
  sleepToHour: 6,
  sleepNotice: ""
};

export default {
  name: "LLMAdmin",
  async mounted() {
    try {
      await this.$store.dispatch("getAllUsers");
      await this.$store.dispatch("getLLMApiKeys");
    } catch (error) {
      await this.$store.dispatch("createError", error);
    }
  },
  data: () => ({
    changedDescription: [],
    editedKey: _.cloneDeep(defaultConfig),
    errorList: [],
    validationDialog: false,
    dialog: false,
    prompts: [],
    promptsLoading: false,
    ragGroups: {}, // { principal_id: [{ _id, name, promptShortId, promptName }] }
    ragGroupsLoading: false,
    ragGroupsMap: {}, // { principal_id: ["Group1", "Group2", ...] } - selected groups per principal
    MAIN_GREEN: "#97BD0F"
  }),
  methods: {
    async reloadApiKeys() {
      await this.$store.dispatch("getLLMApiKeys");
    },
    async notifySuccess(message) {
      await this.$store.dispatch("createSuccess", message);
    },
    getRelevantPrincipalIds() {
      return [this.currentPrincipal, ...(this.editedKey.submandants || [])];
    },
    initializeRagGroupsMap() {
      this.ragGroupsMap = {};
      const ragGroups = Array.isArray(this.editedKey.ragGroups)
        ? this.editedKey.ragGroups
        : [];

      for (const ragGroup of ragGroups) {
        if (ragGroup?.principal_id) {
          this.ragGroupsMap[ragGroup.principal_id] = ragGroup.groups || [];
        }
      }

      for (const principalId of this.getRelevantPrincipalIds()) {
        this.$set(
          this.ragGroupsMap,
          principalId,
          this.ragGroupsMap[principalId] || []
        );
      }
    },
    buildRagGroupsData() {
      return this.getRelevantPrincipalIds()
        .filter(principalId => this.ragGroupsMap[principalId])
        .map(principalId => ({
          principal_id: principalId,
          groups: this.ragGroupsMap[principalId]
        }));
    },
    addChangedDescription(index) {
      if (!this.changedDescription.includes(index))
        this.changedDescription.push(index);
    },
    normalizeSleepHour(value, fallback) {
      const hour = Number(value);
      if (!Number.isFinite(hour)) {
        return fallback;
      }
      return Math.min(23, Math.max(0, Math.floor(hour)));
    },
    async addApiKey() {
      const newApiKey = {
        ...defaultConfig,
        apiKey: String.random(),
        description: "",
        principal_id: this.currentPrincipal,
        submandants: [],
        ragGroups: [],
        ipWhitelist: "",
        isDeactivated: false,
        deactivatedText: "",
        isMonitored: false,
        monitoredWords: "",
        informAddresses: "",
        isSleepEnabled: false,
        sleepFromHour: 22,
        sleepToHour: 6,
        sleepNotice: ""
      };
      try {
        const headers = getTokenHeader();
        const response = await axios.post(
          `${env.prompts_api_client_url}api/eo-prompt/admin`,
          newApiKey,
          { headers }
        );
        if (response.status === 201) {
          await this.notifySuccess("Erfolgreich gespeichert!");
          await this.reloadApiKeys();
        } else {
          await this.$store.dispatch("createError");
        }
      } catch (e) {
        await this.$store.dispatch("createError", e);
      }
    },
    async copyApiKey(apiKey) {
      let clone = {
        ...apiKey,
        apiKey: String.random(),
        description: apiKey.description + " (Kopie)"
      };
      delete clone._id;
      delete clone.updatedBy;
      delete clone.createdBy;

      try {
        const headers = getTokenHeader();
        const response = await axios.post(
          `${env.prompts_api_client_url}api/eo-prompt/admin`,
          clone,
          {
            headers
          }
        );
        if (response.status === 201) {
          await this.notifySuccess("Erfolgreich gespeichert!");
          await this.reloadApiKeys();
        } else {
          await this.$store.dispatch("createError");
        }
      } catch (e) {
        await this.$store.dispatch("createError", e);
      }
    },

    async removeApiKey(key) {
      const isConfirmed = await this.$confirm(
        "Wollen Sie diesen API Key wirklich löschen?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        try {
          const headers = getTokenHeader();
          const response = await axios.delete(
            `${env.prompts_api_client_url}api/eo-prompt/admin/${key._id}`,
            { headers }
          );
          if (response.status === 200) {
            await this.notifySuccess("Erfolgreich gelöscht!");
            await this.reloadApiKeys();
          }
        } catch (e) {
          await this.$store.dispatch("createError", e);
        }
      }
    },
    async loadRagGroups() {
      this.ragGroupsLoading = true;
      try {
        this.ragGroups = {};

        for (const principalId of this.getRelevantPrincipalIds()) {
          const groups = await this.fetchRagGroups(principalId);
          this.$set(this.ragGroups, principalId, groups);
        }
        this.ragGroups = { ...this.ragGroups };
      } catch (err) {
        console.error("Failed to load RAG groups:", err);
      } finally {
        this.ragGroupsLoading = false;
      }
    },
    getSubmandantName(submandantId) {
      const principal = this.principals.find(p => p.id === submandantId);
      return principal?.name || submandantId;
    },
    updateRagGroup(principalId, groups) {
      this.$set(this.ragGroupsMap, principalId, groups);
    },
    async onSubmandantsChange() {
      // Use $nextTick to ensure editedKey.submandants is updated before loading
      await this.$nextTick();
      await this.loadRagGroups();
    },
    async fetchPrompts() {
      try {
        this.promptsLoading = true;
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(
          `${env.prompts_api_client_url}api/eo-prompt/prompts`,
          { params, headers }
        );
        this.prompts = res.data || [];
      } catch (err) {
        console.error("Failed to fetch prompts:", err);
        this.prompts = [];
      } finally {
        this.promptsLoading = false;
      }
    },
    async fetchRagGroups(principalId) {
      try {
        const headers = getTokenHeader();
        const params = { principal_id: principalId };
        const res = await axios.get(
          `${env.prompts_api_client_url}api/eo-prompt/pipeline-groups`,
          { params, headers }
        );
        return res.data || [];
      } catch (err) {
        console.error("Failed to fetch RAG groups for", principalId, ":", err);
        return [];
      }
    },
    async editApiKey(index) {
      this.editedIndex = index;
      this.editedKey = { ...defaultConfig, ...this.apiKeys[index] };
      this.initializeRagGroupsMap();

      this.dialog = true;
      await this.fetchPrompts();
      await this.loadRagGroups();
    },
    closeKey() {
      this.editedIndex = null;
      this.editedKey = Object.assign({}, { ...defaultConfig });
      this.ragGroupsMap = {};
      this.dialog = false;
    },

    async saveKey(close) {
      try {
        const headers = getTokenHeader();
        const dataToSend = {
          ...this.editedKey,
          isDeactivated: !!this.editedKey.isDeactivated,
          deactivatedText: this.editedKey.deactivatedText || "",
          isMonitored: !!this.editedKey.isMonitored,
          monitoredWords: this.editedKey.monitoredWords || "",
          informAddresses: this.editedKey.informAddresses || "",
          ipWhitelist: this.editedKey.ipWhitelist || "",
          isSleepEnabled: !!this.editedKey.isSleepEnabled,
          sleepFromHour: this.normalizeSleepHour(
            this.editedKey.sleepFromHour,
            22
          ),
          sleepToHour: this.normalizeSleepHour(this.editedKey.sleepToHour, 6),
          sleepNotice: this.editedKey.sleepNotice || "",
          ragGroups: this.buildRagGroupsData()
        };

        const response = await axios.put(
          `${env.prompts_api_client_url}api/eo-prompt/admin/${this.editedKey._id}`,
          dataToSend,
          { headers }
        );
        if (response.status !== 200) {
          await this.$store.dispatch("createError", {
            text: "Beim Speichern ist ein Fehler aufgetreten.",
            error: response.data
          });
        } else {
          await this.notifySuccess("Erfolgreich gespeichert!");
          await this.reloadApiKeys();
        }
        if (close) {
          this.closeKey();
        }
      } catch (ex) {
        await this.$store.dispatch("createError", {
          text: "Beim Speichern ist ein Fehler aufgetreten.",
          error: ex
        });
      }
    }
  },
  computed: {
    apiKeys() {
      return this.$store.state.llmApiKeys;
    },
    principals() {
      return this.$store.state.principals;
    },
    isAdmin() {
      return this.$store.state.currentUser?.scopes.includes("role:admin");
    },
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    }
  },
  components: { InfoDialog, APIKeyList, ValidationDialog }
};
